import { Plus, Layers, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Type, Code } from '@/shared/schema';
import { ConditionGroup, LogicalOperator, OperatorType, RuleStructure } from './nestedRuleFunctions';
import RuleCondition from './RuleCondition';
import DropdownSelect from '@/components/uiCustomComps/DropdownSelect';

interface RuleGroupProps {
    group: ConditionGroup;
    level: number;
    groupIndex?: number;
    rule: RuleStructure;
    dataType: Type[];
    attribute: Code[];
    operators: OperatorType[];
    onAddCondition: (groupId: string) => void;
    onAddSubgroup: (groupId: string) => void;
    onUpdateCondition: (condId: string, field: string, value: any) => void;
    onUpdateDatatype: (condId: string, field: string, value: any) => void;
    onUpdateLogicalOperator: (groupId: string, index: number, operator: LogicalOperator) => void;
    onUpdateGroupOperator: (index: number, operator: LogicalOperator) => void;
    onDeleteCondition: (groupId: string, condId: string) => void;
    onDeleteGroup?: (groupId: string) => void;
    isFirstGroup?: boolean;
    version: boolean;
}

const RuleGroup = ({
    group,
    level,
    groupIndex,
    rule,
    dataType,
    attribute,
    operators,
    onAddCondition,
    onAddSubgroup,
    onUpdateCondition,
    onUpdateDatatype,
    onUpdateLogicalOperator,
    onUpdateGroupOperator,
    onDeleteCondition,
    onDeleteGroup,
    isFirstGroup,
    version
}: RuleGroupProps) => {
    const bgColor = level === 0 ? 'bg-gray-50' : 'bg-blue-50';
    const borderColor = level === 0 ? 'border-gray-300' : 'border-blue-300';

    return (
        <div className="space-y-2">

            {level === 0 && groupIndex !== undefined && groupIndex > 0 && (
                <div className="flex items-center gap-2">
                    <DropdownSelect
                        value={rule.groupOperators[groupIndex - 1] || 'AND'}
                        onValueChange={(value) => {
                            onUpdateGroupOperator(groupIndex - 1, value as LogicalOperator)
                        }}
                        options={[{ value: "AND", label: "AND" }, { value: "OR", label: "OR" }]}
                    />
                </div>
            )}

            <div className={`${bgColor} border-2 ${borderColor} rounded-lg py-2 px-4 space-y-1`}>

                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold flex items-center gap-2">
                        {level === 0 ? <Layers className="w-5 h-5" /> : null}
                        {group.type === 'group'
                            ? `Group ${groupIndex !== undefined ? groupIndex + 1 : ''}`
                            : 'Subgroup'}
                    </h3>
                    {group.id !== "group-1" && onDeleteGroup && (
                        <Button
                            onClick={() => onDeleteGroup(group.id)}
                            variant="outline"
                            size="sm"
                            className="p-1.5 hover:bg-red-50 rounded text-red-500"
                            title={`Delete ${group.type === 'group' ? 'Group' : 'Subgroup'}`}
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    )}
                </div>

                {group.conditions.map((item, index) => {
                    if ('conditions' in item) {
                        return (
                            <div key={item.id} className="ml-6">
                                {index > 0 && (
                                    <div className="flex items-center gap-2 mb-2">
                                        <DropdownSelect
                                            value={group.logicalOperators[index - 1] || 'AND'}
                                            onValueChange={(value) => {
                                                onUpdateLogicalOperator(group.id, index - 1, value as LogicalOperator)
                                            }}
                                            options={[{ value: "AND", label: "AND" }, { value: "OR", label: "OR" }]}
                                        />
                                    </div>
                                )}
                                <RuleGroup
                                    group={item}
                                    level={level + 1}
                                    rule={rule}
                                    dataType={dataType}
                                    attribute={attribute}
                                    operators={operators}
                                    onAddCondition={onAddCondition}
                                    onAddSubgroup={onAddSubgroup}
                                    onUpdateCondition={onUpdateCondition}
                                    onUpdateDatatype={onUpdateDatatype}
                                    onUpdateLogicalOperator={onUpdateLogicalOperator}
                                    onUpdateGroupOperator={onUpdateGroupOperator}
                                    onDeleteCondition={onDeleteCondition}
                                    onDeleteGroup={onDeleteGroup}
                                    isFirstGroup={false}
                                    version={version}
                                />
                            </div>
                        );
                    } else {
                        return (
                            <RuleCondition
                                key={item.id}
                                condition={item}
                                groupId={group.id}
                                index={index}
                                groupOperators={group.logicalOperators}
                                isFirstInGroup={index === 0}
                                dataType={dataType}
                                operators={operators}
                                onUpdateCondition={onUpdateCondition}
                                onUpdateDatatype={onUpdateDatatype}
                                onUpdateLogicalOperator={onUpdateLogicalOperator}
                                onDeleteCondition={onDeleteCondition}
                                version={version}
                            />
                        );
                    }
                })}

                <div className="flex gap-2 mt-4 pt-3 border-t">
                    <Button
                        onClick={() => onAddCondition(group.id)}
                        variant="outline"
                        size={"sm"}
                        className="flex items-center gap-2 px-4 py-2 bg-white border rounded hover:bg-gray-50"
                    >
                        <Plus className="w-4 h-4" />
                        Add Condition
                    </Button>
                    <Button
                        onClick={() => onAddSubgroup(group.id)}
                        variant="outline"
                        size={"sm"}
                        className="flex items-center gap-2 px-4 py-2 bg-white border rounded hover:bg-gray-50"
                    >
                        <Layers className="w-4 h-4" />
                        Add Subgroup
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default RuleGroup;